<template>
  <div class="table-container">
    <el-table :data="data" v-loading="loading" style="width: 100%" border>
      <el-table-column prop="id" :label="$t('PartnerManagement.serialNumber')" width="80" align="center">
        <template #default="scope">
          <span 
            style="cursor: pointer; color: #409EFF; text-decoration: none;"
            @click="handleView(scope.row)">
            {{ scope.row.id }}
          </span>
        </template>
      </el-table-column>
      
      <el-table-column prop="contactName" :label="$t('PartnerManagement.contactName')" width="120" align="center"/>
      
      <el-table-column prop="storeName" :label="$t('PartnerManagement.storeName')" min-width="150" show-overflow-tooltip/>
      
      <el-table-column prop="email" :label="$t('PartnerManagement.email')" min-width="160" show-overflow-tooltip/>
      
      <el-table-column prop="tel" :label="$t('PartnerManagement.tel')" width="130" align="center"/>
      
      <el-table-column :label="$t('PartnerManagement.country')" width="100" align="center">
        <template #default="scope">
          {{ getCountryName(scope.row.country) }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('PartnerManagement.customerType')" width="100" align="center">
        <template #default="scope">
          <el-tag :type="Number(scope.row.is_upgrade) === 1 ? 'success' : 'info'" size="small">
            {{ Number(scope.row.is_upgrade) === 1 ? $t('PartnerManagement.upgrade') : $t('PartnerManagement.guest') }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applicantStatus" :label="$t('PartnerManagement.applicantStatus')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="getStatusType(scope.row.applicantStatus)">
            {{ scope.row.statusText }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="applyTime" :label="$t('PartnerManagement.applyTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.applyTime) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewer" :label="$t('PartnerManagement.reviewer')" width="120" align="center">
        <template #default="scope">
          {{ scope.row.reviewer || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewTime" :label="$t('PartnerManagement.reviewTime')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.reviewTime) || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="reviewComment" :label="$t('PartnerManagement.reviewComment')" min-width="150" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.reviewComment || '-' }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('PartnerManagement.dealerRating')" width="150" align="center">
        <template #default="scope">
          <div class="dealer-rating">
            <span 
              v-for="i in 5" 
              :key="i" 
              class="star"
              :class="i <= getDealerRating(scope.row) ? 'star-gold' : 'star-gray'"
              @click="handleDealerRatingChange(scope.row, i)"
            >
              ★
            </span>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog 
      :model-value="dialogVisible" 
      :title="$t('PartnerManagement.reviewApplication')" 
      width="900px" 
      :show-close="true" 
      @close="closeDialog">
      <div v-loading="dialogLoading">
      <el-form :model="reviewForm" :rules="reviewRules" ref="reviewFormRef" label-width="180px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.contactName')">
              <el-input v-model="currentApplication.contactName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.storeName')">
              <el-input v-model="currentApplication.storeName" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.email')">
              <el-input v-model="currentApplication.email" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.tel')">
              <el-input v-model="currentApplication.tel" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.country')">
              <el-input :value="getCountryName(currentApplication.country)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.location')">
              <el-input v-model="currentApplication.location" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.postCode')">
              <el-input v-model="currentApplication.postCode" disabled />
              <span v-if="!postCodeValidation.valid" style="color: #F56C6C; font-size: 12px; margin-top: 4px; display: block;">{{ postCodeValidation.message }}</span>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.taxNumber')">
              <el-input v-model="currentApplication.taxNumber" disabled />
              <div class="vat-buttons">
                <el-button type="success" size="small" @click="openVatLink('eu')">{{ $t('PartnerManagement.vatValidationEu') }}</el-button>
                <el-button type="success" size="small" @click="openVatLink('nonEu')">{{ $t('PartnerManagement.vatValidationNonEu') }}</el-button>
                <el-button type="success" size="small" @click="openVatLink('uk')">{{ $t('PartnerManagement.vatValidationUk') }}</el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.website')">
              <el-input v-model="currentApplication.website" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.businessLicense')">
              <div v-if="currentApplication.businessLicense" class="license-files">
                <el-link 
                  v-for="(license, index) in getBusinessLicenseList(currentApplication.businessLicense)" 
                  :key="index"
                  @click="previewBusinessLicense(license)" 
                  type="primary" 
                  :underline="false" 
                  style="cursor: pointer; display: block; margin-bottom: 4px;"
                >
                  <span style="margin-right: 8px;">{{ index + 1 }}.</span>
                  {{ getBusinessLicenseFileName(license) }}
                </el-link>
              </div>
              <span v-else>-</span>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.workWithAnanda')">
              <el-input :value="getWorkWithAnandaText(currentApplication)" disabled />
            </el-form-item>
          </el-col>
          <template v-if="getWorkWithAnandaText(currentApplication) === $t('PartnerManagement.yes')">
            <el-col :span="24">
              <el-form-item :label="$t('PartnerManagement.howKnowAnanda')">
                <el-input :value="getHowKnowText(currentApplication.howKnowAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('PartnerManagement.whyChooseAnanda')">
                <el-input :value="getWhyChooseText(currentApplication.whyChooseAnanda)" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('PartnerManagement.additionalInfo')">
                <el-input v-model="currentApplication.additionalInfo" type="textarea" :rows="3" disabled />
              </el-form-item>
            </el-col>
            <el-col :span="24">
              <el-form-item :label="$t('PartnerManagement.subscribeNewsletter')">
                <el-input :value="currentApplication.subscribeNewsletter === true || currentApplication.subscribeNewsletter === 'yes' ? $t('PartnerManagement.yes') : $t('PartnerManagement.no')" disabled />
              </el-form-item>
            </el-col>
          </template>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.officeArea')">
              <el-input :value="getAreaText(currentApplication.officeArea)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.warehouse')">
              <el-input :value="getAreaText(currentApplication.warehouse)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.staff')">
              <el-input :value="getStaffText(currentApplication.staff)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.serviceCapability')">
              <el-input :value="getServiceCapabilitiesText(currentApplication.serviceCapability || currentApplication.service_capability)" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.remark')">
              <el-input :value="currentApplication.remark || currentApplication.note || ''" type="textarea" :rows="3" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.applicantStatus')">
              <el-input v-model="currentApplication.statusText" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item :label="$t('PartnerManagement.applyTime')">
              <el-input :value="formatDateTime(currentApplication.applyTime)" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.reviewer')">
              <el-input v-model="currentApplication.reviewer" disabled />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('PartnerManagement.reviewTime')">
              <el-input :value="currentApplication.reviewTime ? formatDateTime(currentApplication.reviewTime) : '-'" disabled />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item :label="$t('PartnerManagement.reviewComment')" prop="reviewComment">
          <el-input 
            v-model="reviewForm.reviewComment" 
            type="textarea" 
            :rows="4" 
            :placeholder="$t('PartnerManagement.reviewCommentPlaceholder')"
            maxlength="200"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      </div>
      <template #footer>
        <el-button
          @click="submitReview"
          :loading="dialogLoading"
          type="danger">
          <el-icon><Close /></el-icon>
          {{ $t('PartnerManagement.reject') }}
        </el-button>
        <el-button @click="closeDialog" type="default">{{ $t('PartnerManagement.cancel') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { Close } from '@element-plus/icons-vue';
import dialogMixin from '@/mixins/dialogMixin';
import countryMixin from '@/mixins/countryMixin';
import { rejectMemberApply, getMemberApplyDetail, getSignUrl, updateDealerRating } from '@/api/api';

export default {
  mixins: [dialogMixin, countryMixin],
  components: {
    Close
  },
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: false,
      currentApplication: {},
      reviewForm: {
        reviewComment: '',
        rate: 0
      },
      reviewRules: {
        reviewComment: [
          { required: true, message: this.$t('PartnerManagement.reviewCommentRequired'), trigger: 'blur' },
          { min: 5, message: this.$t('PartnerManagement.reviewCommentMinLength'), trigger: 'blur' }
        ]
      },
      dialogLoading: false,
      postCodeValidation: { valid: true, message: '' }
    };
  },
  computed: {},
  mounted() {
    this.fetchCountryOptions();
  },
  methods: {
    getErrorMessage(errorCode, message) {
      if (errorCode) {
        const translated = this.$t(`PartnerManagement.${errorCode}`);
        if (translated !== `PartnerManagement.${errorCode}`) {
          return translated;
        }
      }
      return message;
    },

    getDealerRating(row) {
      return Number(row.rate) || Number(row.dealer_rating) || Number(row.dealerRating) || 0;
    },

    async handleDealerRatingChange(row, rating) {
      if (rating < 0 || rating > 5) {
        ElMessage.warning(this.$t('PartnerManagement.dealerRatingMaxWarning'));
        return;
      }
      try {
        const response = await updateDealerRating(row.id, { rate: rating });
        if (response?.code === -1) {
          ElMessage.error(response?.message || this.$t('PartnerManagement.dealerRatingUpdateFailed'));
        } else {
          row.rate = rating;
          row.dealer_rating = rating;
          ElMessage.success(this.$t('PartnerManagement.dealerRatingUpdateSuccess'));
          this.$emit('refresh');
        }
      } catch (error) {
        console.error('更新评级失败:', error);
        ElMessage.error(this.$t('PartnerManagement.dealerRatingUpdateFailed'));
      }
    },

    getStatusType(status) {
      const numericStatus = Number(status);
      const typeMap = {
        0: 'warning',
        1: 'success',
        2: 'danger',
        'pending': 'warning',
        'approved': 'success',
        'rejected': 'danger'
      };
      return typeMap[numericStatus] || typeMap[status] || 'info';
    },

    getStatusText(status) {
      const numericStatus = Number(status);
      const textMap = {
        0: this.$t('PartnerManagement.statusPending'),
        1: this.$t('PartnerManagement.statusApproved'),
        2: this.$t('PartnerManagement.statusRejected'),
        'pending': this.$t('PartnerManagement.statusPending'),
        'approved': this.$t('PartnerManagement.statusApproved'),
        'rejected': this.$t('PartnerManagement.statusRejected')
      };
      return textMap[numericStatus] || textMap[status] || status || '';
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    getCountryName(countryCode) {
      if (!countryCode) return '-';
      const country = this.countryOptions.find(c => c.code === countryCode);
      return country ? country.label : countryCode;
    },

    getAreaText(value) {
      const areaMap = {
        'less20': '< 20m²',
        '20-50': '20-50m²',
        '50-100': '50-100m²',
        'more100': '> 100m²',
        'warehouse_less50': '< 50m²',
        'warehouse_50-100': '50-100m²',
        'warehouse_100-200': '100-200m²',
        'warehouse_more200': '> 200m²',
        'warehouse_none': '无仓库'
      };
      return areaMap[value] || value || '-';
    },

    getStaffText(value) {
      const staffMap = {
        'staff_less5': '< 5 persons',
        'staff_5-10': '5-10 persons',
        'staff_10-20': '10-20 persons',
        'staff_more20': '> 20 persons'
      };
      return staffMap[value] || value || '-';
    },

    getServiceCapabilitiesText(capabilities) {
      if (!capabilities) return '-';
      const capArray = Array.isArray(capabilities) ? capabilities : capabilities.split(',');
      const capMap = {
        'hotLine': this.$t('MemberRegistration.serviceHotLine'),
        'service5d8h': this.$t('MemberRegistration.service5d8h'),
        'bikeRepair': this.$t('MemberRegistration.bikeRepair'),
        'electricalRepair': this.$t('MemberRegistration.electricalRepair'),
        'spareParts': this.$t('MemberRegistration.spareParts'),
        'onSiteService': this.$t('MemberRegistration.onSiteService')
      };
      return capArray.map(c => capMap[c] || c).join(', ');
    },

    getBusinessLicenseFileName(key) {
      if (!key) return '-';
      return key.split('/').pop();
    },

    openVatLink(type) {
      const urls = {
        eu: 'https://ec.europa.eu/taxation_customs/vies/#/vat-validation',
        nonEu: 'https://vatapp.net/vat-number-check',
        uk: 'https://www.tax.service.gov.uk/check-vat-number/enter-vat-details'
      };
      window.open(urls[type], '_blank');
    },

    async previewBusinessLicense(key) {
      if (!key) return;
      try {
        const result = await getSignUrl(key);
        let signedUrl = '';
        if (typeof result === 'string') {
          signedUrl = result;
        } else if (result && (result.url || result.data?.url)) {
          signedUrl = result.url || result.data?.url;
        }
        if (signedUrl && signedUrl.startsWith('http')) {
          window.open(signedUrl, '_blank');
        } else {
          ElMessage.error(this.$t('PartnerManagement.previewFailed'));
        }
      } catch (error) {
        console.error('获取文件预览链接失败:', error);
        ElMessage.error(this.$t('PartnerManagement.previewFailed'));
      }
    },

    getBusinessLicenseList(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return value.split(',').filter(v => v.trim());
    },

    getWorkWithAnandaText(item) {
      if (item.workWithAnanda === 'yes') return this.$t('PartnerManagement.yes');
      if (item.workWithAnanda === 'no') return this.$t('PartnerManagement.no');
      if (item.isUseAnanda === 1 || item.is_use_ananda === 1) return this.$t('PartnerManagement.yes');
      return this.$t('PartnerManagement.no');
    },

    getHowKnowText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'exhibition': this.$t('MemberRegistration.exhibition'),
        'magazines': this.$t('MemberRegistration.magazines'),
        'socialMedia': this.$t('MemberRegistration.socialMedia'),
        'supplier': this.$t('MemberRegistration.supplier')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    getWhyChooseText(value) {
      if (!value) return '-';
      const valArray = Array.isArray(value) ? value : value.split(',');
      const map = {
        'qualityPrice': this.$t('MemberRegistration.qualityPrice'),
        'differentiation': this.$t('MemberRegistration.differentiation'),
        'supplierAdvice': this.$t('MemberRegistration.supplierAdvice'),
        'other': this.$t('MemberRegistration.other')
      };
      return valArray.map(v => map[v] || v).join(', ');
    },

    async handleView(row) {
      this.dialogLoading = true;
      try {
        // 以 partner_info.id 作为申请记录主键；若缺失则退回到 row.id 以兼容老数据
        const applyRecordId = row.partner_info_id || row.id;

        let detailData = {};
        if (applyRecordId) {
          try {
            const response = await getMemberApplyDetail(applyRecordId);
            detailData = response?.data || response || {};
          } catch (err) {
            // 详情接口失败时，仍然用 row 及 partner_info 内嵌数据兜底渲染
            console.warn('获取详情接口失败，使用列表数据兜底:', err);
            detailData = {};
          }
        }

        // 合并三个数据源：列表行 row、详情接口 detailData、内嵌 detailData.partner_info
        const detailPartnerInfo = detailData.partner_info || {};
        const rowPartnerInfo = row.partner_info || {};
        const p = { ...rowPartnerInfo, ...detailPartnerInfo, ...detailData };

        // ====== 读取各字段（优先级：详情 > partner_info > row）======
        const auditStatus = (detailData.audit_status !== undefined && detailData.audit_status !== null)
          ? detailData.audit_status
          : ((p.audit_status !== undefined && p.audit_status !== null)
            ? p.audit_status
            : (row.applicantStatus !== undefined ? row.applicantStatus : (detailData.status || row.status)));

        const workWithAnandaRaw = (p.is_use_ananda !== undefined && p.is_use_ananda !== null)
          ? Number(p.is_use_ananda)
          : (detailData.isUseAnanda !== undefined ? Number(detailData.isUseAnanda)
          : (row.is_use_ananda !== undefined ? Number(row.is_use_ananda) : null));
        const workWithAnanda = (workWithAnandaRaw === 1) ? 'yes'
          : (workWithAnandaRaw === 0 ? 'no'
          : ((detailData.workWithAnanda === 'yes' || detailData.workWithAnanda === 'no') ? detailData.workWithAnanda
          : row.workWithAnanda || 'no'));

        const serviceCapRaw = p.service_capability
          || detailData.serviceCapability
          || detailData.service_capability
          || row.serviceCapability
          || (Array.isArray(row.serviceCapabilities) ? row.serviceCapabilities.join(',') : row.serviceCapabilities)
          || '';

        const howKnowRaw = p.know_channel
          || detailData.know_channel
          || detailData.howKnowAnanda
          || row.howKnowAnanda
          || '';

        const whyChooseRaw = p.cooperate_reason
          || detailData.cooperate_reason
          || detailData.whyChooseAnanda
          || row.whyChooseAnanda
          || '';

        const additionalRaw = p.reason_extra
          || detailData.reason_extra
          || detailData.additionalInfo
          || detailData.additional_info
          || row.additionalInfo
          || '';

        const subscribeRaw = (p.is_subscribe !== undefined && p.is_subscribe !== null)
          ? Number(p.is_subscribe)
          : (detailData.is_subscribe !== undefined ? Number(detailData.is_subscribe)
          : (typeof detailData.subscribeNewsletter === 'boolean' ? (detailData.subscribeNewsletter ? 1 : 0)
          : (typeof row.subscribeNewsletter === 'boolean' ? (row.subscribeNewsletter ? 1 : 0) : 0)));

        const countryCode = p.country
          || detailData.country
          || detailData.country_code
          || row.country
          || '';

        const reviewerName = p.audit_user_name
          || detailData.audit_user_name
          || detailData.reviewer
          || detailData.auditor
          || '';
        const reviewerId = p.audit_user_id
          || detailData.audit_user_id
          || row.audit_user_id
          || '';
        const reviewerFinal = reviewerName || (reviewerId ? String(reviewerId) : (row.reviewer || ''));

        this.currentApplication = {
          ...row,
          ...detailData,
          partner_info_id: row.partner_info_id || p.id || null,
          id: row.id,
          contactName: p.contact_name || detailData.contactName || detailData.contact_name || row.contactName || '',
          storeName: p.store_name || detailData.storeName || detailData.store_name || row.storeName || '',
          country: countryCode,
          email: p.email || detailData.email || row.email || '',
          tel: p.tel || detailData.tel || detailData.phone || row.tel || '',
          location: p.location || detailData.location || detailData.address || row.location || '',
          postCode: p.post_code || detailData.postCode || detailData.post_code || row.postCode || '',
          taxNumber: p.tax_number || detailData.taxNumber || detailData.tax_number || row.taxNumber || '',
          officeArea: p.office_area || detailData.officeArea || detailData.office_area || row.officeArea || '',
          warehouse: p.warehouse_area || detailData.warehouse || detailData.warehouse_area || row.warehouse || '',
          staff: p.staff_count || detailData.staff || detailData.staff_count || row.staff || '',
          is_use_ananda: workWithAnandaRaw,
          workWithAnanda: workWithAnanda,
          howKnowAnanda: howKnowRaw,
          whyChooseAnanda: whyChooseRaw,
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          applyTime: p.apply_time || detailData.applyTime || detailData.apply_time || row.applyTime || '',
          reviewer: reviewerFinal,
          audit_user_id: reviewerId || null,
          reviewTime: p.audit_time || detailData.reviewTime || detailData.audit_time || row.reviewTime || '',
          website: p.website || detailData.website || row.website || '',
          businessLicense: p.business_license || detailData.businessLicense || detailData.business_license || row.businessLicense || '',
          subscribeNewsletter: (subscribeRaw === 1),
          serviceCapability: Array.isArray(serviceCapRaw) ? serviceCapRaw.join(',') : serviceCapRaw,
          service_capability: Array.isArray(serviceCapRaw) ? serviceCapRaw.join(',') : serviceCapRaw,
          additionalInfo: additionalRaw,
          remark: p.note || detailData.remark || detailData.note || row.remark || row.note || '',
          note: p.note || detailData.note || row.note || '',
          reviewComment: p.audit_comment || detailData.reviewComment || detailData.audit_comment || row.reviewComment || ''
        };

        if (!this.countriesLoaded) {
          await this.fetchCountryOptions();
        }
        this.postCodeValidation = this.validatePostalCode(this.currentApplication.country, this.currentApplication.postCode);
      } catch (error) {
        console.error('获取详情失败:', error);
        // 失败兜底：至少把 row 的映射字段再次保证 statusText/serviceCapability/workWithAnanda 补齐
        const auditStatus = row.applicantStatus !== undefined ? row.applicantStatus : (row.status || 0);
        const svcCap = (Array.isArray(row.serviceCapabilities) ? row.serviceCapabilities.join(',') : (row.serviceCapability || ''));
        this.currentApplication = {
          ...row,
          partner_info_id: row.partner_info_id || null,
          applicantStatus: auditStatus,
          statusText: this.getStatusText(auditStatus),
          serviceCapability: svcCap,
          service_capability: svcCap,
          workWithAnanda: row.workWithAnanda || (row.is_use_ananda === 1 ? 'yes' : 'no'),
          reviewer: row.reviewer || (row.audit_user_id ? String(row.audit_user_id) : ''),
          remark: row.remark || row.note || ''
        };
        this.postCodeValidation = { valid: true, message: '' };
        ElMessage.error(this.$t('PartnerManagement.reviewFailed'));
      } finally {
        this.dialogLoading = false;
      }
      this.reviewForm = {
        reviewComment: this.currentApplication.reviewComment || ''
      };
      this.dialogVisible = true;
    },

    closeDialog() {
      this.dialogVisible = false;
      this.currentApplication = {};
      this.reviewForm = {
        reviewComment: '',
        rate: 0
      };
    },

    async submitReview() {
      try {
        if (!this.$refs.reviewFormRef) {
          ElMessage.error(this.$t('PartnerManagement.formValidationError'));
          return;
        }

        await this.$refs.reviewFormRef.validate();

        this.dialogLoading = true;

        const applyId = this.currentApplication.partner_info_id || this.currentApplication.id;
        const reviewComment = this.reviewForm.reviewComment;
        const reviewerName = localStorage.getItem('userName') || '';

        const response = await rejectMemberApply(applyId, { auditComment: reviewComment, reviewer: reviewerName, rate: this.reviewForm.rate });
        
        const msg = this.getErrorMessage(response?.error_code, response?.message || response?.msg);
        if (response?.code === -1) {
          ElMessage.error(msg || this.$t('PartnerManagement.reviewFailed'));
        } else {
          ElMessage.success(msg || this.$t('PartnerManagement.reviewSuccess'));
          this.closeDialog();
          this.$emit('refresh');
        }
        
      } catch (error) {
        if (error !== false) {
          console.error('审核失败:', error);
          if (!(error && error.message === this.$t('PartnerManagement.reviewCommentMinLength'))) {
            const status = error?.response?.status;
            if (status === 500) {
              ElMessage.error(this.$t('PartnerManagement.reviewFailed'));
            } else {
              const errorMsg = this.getErrorMessage(error?.response?.data?.error_code, error?.response?.data?.message || error?.response?.data?.msg || error?.message || this.$t('PartnerManagement.reviewFailed'));
              ElMessage.error(errorMsg);
            }
          }
        }
      } finally {
        this.dialogLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 20px;
}

.dealer-rating {
  display: inline-flex;
  gap: 2px;
}

.star {
  font-size: 20px;
  cursor: pointer;
  user-select: none;
  transition: transform 0.2s;
}

.star:hover {
  transform: scale(1.2);
}

.star-gold {
  color: #FFD700;
}

.star-gray {
  color: #D3D3D3;
}

.vat-buttons {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 180px;
}

.vat-buttons .el-button {
  width: 100%;
  justify-content: flex-start;
  padding-left: 12px;
  margin-left: 0 !important;
}
</style>
